// AdminPostEditor.tsx — /admin/posts/new and /admin/posts/:id, blog/SEO cycle Task 3 (spec
// docs/superpowers/specs/2026-08-06-blog-seo-design.md §5). One component backs both routes
// (App.tsx): `id` is undefined on `/new`.
//
// PREVIEW: 400ms after the body textarea stops changing, the debounced text is POSTed as-is
// to `POST /api/admin/posts/preview` (routes/admin-posts.ts) — a stateless render that never
// touches the database. This used to be wired through a debounced autosave PATCH of
// `{ bodyMd }` plus a GET keyed by the post's id, because that GET could only render whatever
// was already persisted. But autosaving on every keystroke meant editing an ALREADY-PUBLISHED
// post streamed half-written paragraphs to real readers within the public list/detail cache
// window — unacceptable. Previewing is a pure function of the textarea's contents, so it's
// modelled as one: nothing is written, and a brand-new unsaved post (`/admin/posts/new`, no
// id yet) now previews exactly like any other.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { resolvePostVisibility } from '@repo/shared-types';
import type {
  AdminAuthorDto,
  AdminPostDto,
  AdminPostPreviewDto,
  MediaUploadResponseDto,
  PostStatus,
} from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/components/dialog';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { StatusChip } from '@repo/ui/components/status-chip';
import { apiFetch, ApiError } from '../lib/api';
import { useNow } from '../lib/use-now';
import { useLocale } from '../locales/context';
import type { Catalog } from '../locales/en';
import { formatDate as formatDateIntl, formatDateTime as formatDateTimeIntl } from '../locales/format';
import type { Locale } from '../locales';

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const PREVIEW_DEBOUNCE_MS = 400;
// Blog-polish spec §F — matches `adminCreateAuthorSchema`/`adminUpdateAuthorSchema`'s
// `persona` cap (packages/validation/src/blog.ts).
const PERSONA_MAX = 1200;

/** Lowercase, hyphen-separated — same shape `postSlugSchema` requires server-side
 * (packages/validation/src/blog.ts). Used both for the title→slug auto-derivation and for
 * the "new author" dialog's slug. */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Sized to `PostForm`'s real title/slug row, excerpt block, and markdown/preview columns
 * below (handoff Shell Anatomy §06). */
function PostFormSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <SkeletonText className="w-10" />
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="flex flex-col gap-2">
          <SkeletonText className="w-10" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <SkeletonText className="w-14" />
        <Skeleton className="h-16 w-full" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}

function formatDate(iso: string | null, locale: Locale): string {
  if (!iso) return '—';
  return formatDateIntl(iso, locale, { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso: string, locale: Locale): string {
  return formatDateTimeIntl(iso, locale, {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** `<input type="datetime-local">` speaks local wall-clock time with no zone, so both
 * directions go through the Date object rather than slicing the ISO string — slicing would
 * silently shift the value by the browser's UTC offset. */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `datetime-local` has no timezone: the value typed in is wall-clock time in whatever zone
 * the browser is set to, and `localInputToIso` converts it to the UTC instant that gets
 * stored. That is correct but invisible, and scheduling a post for the wrong hemisphere's
 * morning is a silent mistake — so the field says which clock it is using. */
function localZoneLabel(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offsetMinutes = -new Date().getTimezoneOffset();
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  const offset = `UTC${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
  return zone ? `${zone} (${offset})` : offset;
}

function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const TEXTAREA_CLASSES =
  'w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50';

export function AdminPostEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === undefined;
  const { t } = useLocale();

  const postQuery = useQuery({
    queryKey: ['admin', 'post', id],
    queryFn: () => apiFetch<AdminPostDto>(`/api/admin/posts/${id}`),
    enabled: !isNew,
  });

  const authorsQuery = useQuery({
    queryKey: ['admin', 'authors'],
    queryFn: () => apiFetch<AdminAuthorDto[]>('/api/admin/authors'),
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="outline" size="sm">
          <Link to="/admin/posts">{t.admin.postEditor.backToPosts}</Link>
        </Button>
        <h2 className="truncate text-lg font-semibold">
          {isNew
            ? t.admin.postEditor.newPostTitle
            : (postQuery.data?.title ?? t.admin.postEditor.editPostTitleFallback)}
        </h2>
      </div>

      {!isNew && postQuery.isPending && <PostFormSkeleton />}
      {!isNew && postQuery.error && <p className="text-destructive">{postQuery.error.message}</p>}

      {/* `key` remounts the form fresh whenever the underlying post identity changes — most
          notably the `undefined` → real id transition right after the first Save (see
          `handleSave` below), so newly-created state always derives from the server's own
          response instead of a stale local guess. */}
      {(isNew || postQuery.data) && (
        <PostForm
          key={postQuery.data?.id ?? 'new'}
          post={postQuery.data ?? null}
          authors={authorsQuery.data ?? []}
        />
      )}
    </div>
  );
}

/** The field-name tokens `PostForm`'s validation pushes onto its own `missing` array — also the
 * lookup key into `t.admin.postEditor.missingFieldLabel` (see that key's own doc comment). */
type MissingField = 'title' | 'slug' | 'excerpt' | 'body' | 'author';

function PostForm({ post, authors }: { post: AdminPostDto | null; authors: AdminAuthorDto[] }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, locale } = useLocale();
  const isNew = post === null;
  const postId = post?.id;

  const [title, setTitle] = useState(post?.title ?? '');
  const [slug, setSlug] = useState(post?.slug ?? '');
  // A brand-new post derives its slug from the title until the admin edits it directly; an
  // existing post's slug was already a deliberate choice, so it starts "touched".
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [excerpt, setExcerpt] = useState(post?.excerpt ?? '');
  const [tagsInput, setTagsInput] = useState(post?.tags.join(', ') ?? '');
  const [coverUrl, setCoverUrl] = useState(post?.coverUrl ?? '');
  const [authorId, setAuthorId] = useState(post?.authorId ?? '');
  const [status, setStatus] = useState<PostStatus>(post?.status ?? 'draft');
  const [publishedAt, setPublishedAt] = useState<string | null>(post?.publishedAt ?? null);
  const [bodyMd, setBodyMd] = useState(post?.bodyMd ?? '');

  const [formError, setFormError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'save' | 'toggle' | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const now = useNow();

  // Defaults the author <select> to the first author once the list arrives, for a new post
  // where nothing has been picked yet — derived at render time (no effect + setState) so it
  // never fights an explicit choice: `setAuthorId` below always wins once the admin picks one.
  const effectiveAuthorId = authorId || (authors[0]?.id ?? '');
  const selectedAuthor = authors.find((a) => a.id === effectiveAuthorId);

  function handleTitleChange(value: string) {
    setTitle(value);
    if (!slugTouched) setSlug(slugify(value));
  }

  function handleSlugChange(value: string) {
    setSlug(value);
    setSlugTouched(true);
  }

  function buildPayload(explicitStatus: PostStatus) {
    return {
      slug: slug.trim(),
      title: title.trim(),
      excerpt: excerpt.trim(),
      bodyMd,
      coverUrl: coverUrl.trim() ? coverUrl.trim() : null,
      tags: tagsInput
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0),
      authorId: effectiveAuthorId,
      status: explicitStatus,
      // Sent on every save so an edited publish time sticks. Left null on a draft, which is
      // what makes the server stamp `now()` the first time it goes live — a post is scheduled
      // only when someone deliberately puts a future time in the field.
      publishedAt,
    };
  }

  const missing: MissingField[] = [];
  if (!title.trim()) missing.push('title');
  if (!slug.trim() || !SLUG_PATTERN.test(slug.trim())) missing.push('slug');
  if (!excerpt.trim()) missing.push('excerpt');
  if (!bodyMd.trim()) missing.push('body');
  if (!effectiveAuthorId) missing.push('author');
  const canSave = missing.length === 0;

  const saveMutation = useMutation({
    mutationFn: (explicitStatus: PostStatus) => {
      const payload = buildPayload(explicitStatus);
      return postId
        ? apiFetch<AdminPostDto>(`/api/admin/posts/${postId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
        : apiFetch<AdminPostDto>('/api/admin/posts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
    },
    onSuccess: async (saved) => {
      setFormError(null);
      setPendingAction(null);
      setStatus(saved.status);
      setPublishedAt(saved.publishedAt);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'posts'] });
      if (!postId) {
        // Pre-seed the cache so the remount triggered by the URL change below (see
        // `AdminPostEditor`'s `key`) has data immediately — no loading flash between
        // "just created" and "now editing an existing post".
        queryClient.setQueryData(['admin', 'post', saved.id], saved);
        navigate(`/admin/posts/${saved.id}`, { replace: true });
      } else {
        queryClient.setQueryData(['admin', 'post', postId], saved);
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    },
    onError: (err: Error) => {
      setFormError(err.message);
      setPendingAction(null);
    },
  });

  function handleSave() {
    setFormError(null);
    setPendingAction('save');
    saveMutation.mutate(status);
  }

  function handleTogglePublish() {
    setFormError(null);
    setPendingAction('toggle');
    saveMutation.mutate(status === 'published' ? 'draft' : 'published');
  }

  // Derived from both columns, never from `status` alone — see `resolvePostVisibility`.
  // `now` comes from state rather than the clock so the chip flips to "live" by itself when a
  // scheduled time passes with the editor open.
  const visibility = resolvePostVisibility({ status, publishedAt }, now);
  const willSchedule =
    status !== 'published' && publishedAt !== null && new Date(publishedAt).getTime() > now.getTime();

  // --- Preview (see the file-level PREVIEW comment) ------------------------------------------
  // Debounces the raw textarea value, then keys the preview query on that debounced value so
  // React Query only fires (and only re-fires) `POST /preview` once typing settles.
  const [debouncedBodyMd, setDebouncedBodyMd] = useState(bodyMd);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedBodyMd(bodyMd), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [bodyMd]);

  const previewQuery = useQuery({
    queryKey: ['admin', 'post-preview', debouncedBodyMd],
    queryFn: () =>
      apiFetch<AdminPostPreviewDto>('/api/admin/posts/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bodyMd: debouncedBodyMd }),
      }),
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor="post-title">{t.admin.postEditor.titleLabel}</Label>
          <Input
            id="post-title"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            maxLength={200}
            placeholder={t.admin.postEditor.titlePlaceholder}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="post-slug">{t.admin.postEditor.slugLabel}</Label>
          <Input
            id="post-slug"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            maxLength={200}
            placeholder={t.admin.postEditor.slugPlaceholder}
            className="font-mono"
          />
          <p className="truncate text-xs text-muted-foreground">
            {t.admin.postEditor.slugUrlPrefix}{slug || '…'}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="post-excerpt">{t.admin.postEditor.excerptLabel}</Label>
        <textarea
          id="post-excerpt"
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder={t.admin.postEditor.excerptPlaceholder}
          className={TEXTAREA_CLASSES}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor="post-tags">{t.admin.postEditor.tagsLabel}</Label>
          <Input
            id="post-tags"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder={t.admin.postEditor.tagsPlaceholder}
          />
          <p className="text-xs text-muted-foreground">{t.admin.postEditor.tagsHint}</p>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="post-cover">{t.admin.postEditor.coverUrlLabel}</Label>
          <Input
            id="post-cover"
            value={coverUrl}
            onChange={(e) => setCoverUrl(e.target.value)}
            placeholder={t.admin.postEditor.coverUrlPlaceholder}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="post-author">{t.admin.postEditor.authorLabel}</Label>
          <div className="flex items-center gap-3">
            {selectedAuthor && (
              <AuthorDialog
                author={selectedAuthor}
                trigger={
                  <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs">
                    {t.admin.postEditor.editAuthorCta}
                  </Button>
                }
                onSaved={(author) => {
                  queryClient.setQueryData(
                    ['admin', 'authors'],
                    (prev: AdminAuthorDto[] | undefined) =>
                      (prev ?? []).map((a) => (a.id === author.id ? author : a)),
                  );
                }}
                t={t}
              />
            )}
            <AuthorDialog
              trigger={
                <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs">
                  {t.admin.postEditor.newAuthorCta}
                </Button>
              }
              onSaved={(author) => {
                queryClient.setQueryData(
                  ['admin', 'authors'],
                  (prev: AdminAuthorDto[] | undefined) => [...(prev ?? []), author],
                );
                setAuthorId(author.id);
              }}
              t={t}
            />
          </div>
        </div>
        <Select value={effectiveAuthorId} onValueChange={setAuthorId}>
          <SelectTrigger id="post-author" className="w-full sm:w-72">
            <SelectValue placeholder={t.admin.postEditor.authorSelectPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {authors.map((author) => (
              <SelectItem key={author.id} value={author.id}>
                {author.name}
                {author.role ? ` — ${author.role}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {authors.length === 0 && (
          <p className="text-xs text-muted-foreground">{t.admin.postEditor.noAuthorsYet}</p>
        )}
      </div>

      <div className="flex flex-col gap-3 rounded-md border p-3">
        <div className="flex flex-wrap items-center gap-3">
          <StatusChip
            state={visibility === 'live' ? 'ready' : visibility === 'scheduled' ? 'hint' : 'paused'}
            label={visibility}
          />
          <span className="text-sm text-muted-foreground">
            {visibility === 'scheduled'
              ? t.admin.postEditor.visibilityScheduledText({
                  date: formatDateTime(publishedAt as string, locale),
                })
              : visibility === 'live'
                ? t.admin.postEditor.visibilityLiveText({ date: formatDate(publishedAt, locale) })
                : t.admin.postEditor.visibilityDraftText}
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="publishedAt">{t.admin.postEditor.publishDateLabel}</Label>
          <Input
            id="publishedAt"
            type="datetime-local"
            // `max-w-*`, not `w-auto`: the base Input sets `w-full`, which wins the width
            // slot and stretched a date field across the whole card.
            className="max-w-64"
            value={isoToLocalInput(publishedAt)}
            onChange={(e) => setPublishedAt(localInputToIso(e.target.value))}
          />
          <p className="text-xs text-muted-foreground">
            {t.admin.postEditor.publishDateHintPrefix}
            <strong className="font-medium">{localZoneLabel()}</strong>
            {t.admin.postEditor.publishDateHintSuffix}
          </p>
        </div>
      </div>

      <WritingGuidePanel
        authorName={selectedAuthor?.name ?? null}
        persona={selectedAuthor?.persona ?? null}
        t={t}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label>{t.admin.postEditor.bodyLabel}</Label>
          <MarkdownEditor value={bodyMd} onChange={setBodyMd} t={t} />
        </div>
        <div className="flex flex-col gap-2">
          <Label>{t.admin.postEditor.livePreviewLabel}</Label>
          <PreviewPane
            preview={previewQuery.data}
            isPending={previewQuery.isPending}
            error={previewQuery.error}
            t={t}
          />
        </div>
      </div>

      {missing.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t.admin.postEditor.beforeSavingAdd({
            fields: missing.map((field) => t.admin.postEditor.missingFieldLabel[field]).join(', '),
          })}
        </p>
      )}
      {formError && <p className="text-sm text-destructive">{formError}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" disabled={!canSave || saveMutation.isPending} onClick={handleSave}>
          {saveMutation.isPending && pendingAction === 'save'
            ? t.admin.postEditor.savingCta
            : status === 'published'
              ? t.admin.postEditor.saveChangesCta
              : t.admin.postEditor.saveDraftCta}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={!canSave || saveMutation.isPending}
          onClick={handleTogglePublish}
        >
          {saveMutation.isPending && pendingAction === 'toggle'
            ? status === 'published'
              ? t.admin.postEditor.unpublishingCta
              : willSchedule
                ? t.admin.postEditor.schedulingCta
                : t.admin.postEditor.publishingCta
            : status === 'published'
              ? t.admin.postEditor.unpublishCta
              : willSchedule
                ? t.admin.postEditor.scheduleCta
                : t.admin.postEditor.publishCta}
        </Button>
        {savedFlash && <span className="text-sm text-muted-foreground">{t.admin.postEditor.savedFlash}</span>}
        {postId && <DeletePostDialog postId={postId} title={title} t={t} />}
      </div>
    </div>
  );
}

function PreviewPane({
  preview,
  isPending,
  error,
  t,
}: {
  preview: AdminPostPreviewDto | undefined;
  isPending: boolean;
  error: Error | null;
  t: Catalog;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="min-h-[420px] overflow-y-auto rounded-md border p-4">
        {isPending && <p className="text-sm text-muted-foreground">{t.admin.postEditor.renderingPreview}</p>}
        {error && (
          <p className="text-sm text-destructive">
            {t.admin.postEditor.previewFailedPrefix}
            {error.message}
          </p>
        )}
        {preview && (
          // Safe to use dangerouslySetInnerHTML: `preview.html` is server-rendered by
          // `renderMarkdownToHtml` (@repo/markdown), the SAME pipeline the public site renders
          // through, which sanitizes via `rehype-sanitize` against a fixed allow-list schema
          // (packages/markdown/src/schema.ts) — only headings/lists/links/images/code/tables/
          // blockquotes tags survive, `href`/`src` are restricted to http(s)/mailto (no
          // `javascript:`), and `<script>`/`<style>` are hard-stripped, not merely unwrapped.
          // This is trusted output of OUR OWN sanitizer, never raw user HTML, so it carries the
          // same trust level as any other server-rendered HTML in the app.
          <div
            className="max-w-none text-sm leading-relaxed break-words [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground [&_code]:font-mono [&_code]:text-xs [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-base [&_h3]:font-semibold [&_img]:my-3 [&_img]:max-w-full [&_img]:rounded-md [&_li]:mb-1 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 [&_table]:my-3 [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:p-1.5 [&_th]:border [&_th]:border-border [&_th]:p-1.5 [&_th]:text-left [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: preview.html }}
          />
        )}
      </div>
    </div>
  );
}

/** Markdown textarea + image drop/paste upload (spec §5). Uploads go to
 * `POST /api/admin/media` (magic-byte validated server-side, apps/api/src/routes/admin-
 * media.ts) and insert `![](url)` at the caret — never appended to the end — so an image
 * dropped mid-paragraph lands where the cursor was. */
function MarkdownEditor({
  value,
  onChange,
  t,
}: {
  value: string;
  onChange: (next: string) => void;
  t: Catalog;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingCaretRef = useRef<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Runs after the DOM reflects the just-inserted markdown, so the caret lands right after
  // the inserted `![](url)` instead of wherever the browser defaults a controlled-value
  // update's selection to.
  useLayoutEffect(() => {
    if (pendingCaretRef.current !== null && textareaRef.current) {
      const pos = pendingCaretRef.current;
      textareaRef.current.setSelectionRange(pos, pos);
      textareaRef.current.focus();
      pendingCaretRef.current = null;
    }
  }, [value]);

  async function uploadAndInsert(file: File) {
    setUploadError(null);
    setUploading(true);
    const caret = textareaRef.current?.selectionStart ?? value.length;
    try {
      const form = new FormData();
      form.append('file', file);
      const { url } = await apiFetch<MediaUploadResponseDto>('/api/admin/media', {
        method: 'POST',
        body: form,
      });
      const markdown = `![](${url})`;
      pendingCaretRef.current = caret + markdown.length;
      onChange(value.slice(0, caret) + markdown + value.slice(caret));
    } catch (err) {
      // Failure surfaces inline without touching `value` — whatever was already typed stays
      // exactly as-is (spec §5: "failures surface inline without losing the typed text").
      setUploadError(err instanceof ApiError ? err.message : t.admin.postEditor.uploadFailedFallback);
    } finally {
      setUploading(false);
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image/'));
    const file = item?.getAsFile();
    if (!file) return;
    e.preventDefault();
    void uploadAndInsert(file);
  }

  function handleDrop(e: React.DragEvent<HTMLTextAreaElement>) {
    const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith('image/'));
    if (!file) return;
    e.preventDefault();
    void uploadAndInsert(file);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        rows={20}
        placeholder={t.admin.postEditor.bodyPlaceholder}
        className={`min-h-[420px] flex-1 resize-y font-mono ${TEXTAREA_CLASSES}`}
      />
      <div className="flex min-h-5 items-center gap-2 text-xs">
        {uploading && <span className="text-muted-foreground">{t.admin.postEditor.uploadingText}</span>}
        {uploadError && (
          <span className="text-destructive">
            {t.admin.postEditor.imageUploadFailedPrefix}
            {uploadError}
          </span>
        )}
      </div>
    </div>
  );
}

function DeletePostDialog({ postId, title, t }: { postId: string; title: string; t: Catalog }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ deleted: boolean }>(`/api/admin/posts/${postId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'posts'] });
      navigate('/admin/posts');
    },
    onError: (err: Error) => setDeleteError(err.message),
  });

  function openDialog() {
    setDeleteError(null);
    setConfirmTitle('');
    setOpen(true);
  }

  return (
    <>
      <Button
        type="button"
        variant="destructive-soft"
        size="sm"
        className="ml-auto"
        onClick={openDialog}
      >
        {t.admin.postEditor.deleteCta}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.admin.postEditor.deleteDialogTitle({ title })}</DialogTitle>
            <DialogDescription>
              {t.admin.postEditor.deleteDialogDescriptionPrefix}{' '}
              <span className="font-medium text-foreground">{title}</span>
              {t.admin.postEditor.deleteDialogDescriptionSuffix}
            </DialogDescription>
          </DialogHeader>
          <Input
            aria-label={t.admin.postEditor.confirmPostTitleAriaLabel}
            value={confirmTitle}
            onChange={(e) => setConfirmTitle(e.target.value)}
            placeholder={title}
            autoFocus
          />
          {deleteError && <p className="text-[13px] text-destructive">{deleteError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t.admin.postEditor.cancelCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={confirmTitle !== title || deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending ? t.admin.postEditor.deletingCta : t.admin.postEditor.deletePostCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Inline "new/edit author" affordance (spec §5: "an inline 'new author' affordance ... keep
 * it simple"; blog-polish spec §F extends it to editing, since persona now needs somewhere
 * to be set on an author that already exists). One dialog backs both: `author` undefined ⇒
 * create (slug auto-derived from name, not shown — same "keep it simple" instruction), a
 * real `AdminAuthorDto` ⇒ edit that author in place via PATCH. Real people only (spec §1). */
function AuthorDialog({
  author,
  trigger,
  onSaved,
  t,
}: {
  author?: AdminAuthorDto;
  trigger: React.ReactNode;
  onSaved: (author: AdminAuthorDto) => void;
  t: Catalog;
}) {
  const isEdit = author !== undefined;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [persona, setPersona] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Re-derive every field from `author` each time the dialog opens (rather than once at
  // mount) — `trigger`/`author` can outlive a single open/close cycle (the "Edit author"
  // dialog is remounted only when the SELECTED author changes, not on every open), so a
  // stale previous edit's typed-but-uncommitted text must never leak into the next open.
  function handleOpenChange(next: boolean) {
    if (next) {
      setName(author?.name ?? '');
      setRole(author?.role ?? '');
      setBio(author?.bio ?? '');
      setAvatarUrl(author?.avatarUrl ?? '');
      setPersona(author?.persona ?? '');
      setError(null);
    }
    setOpen(next);
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const shared = {
        name: name.trim(),
        role: role.trim() || null,
        bio: bio.trim() || null,
        avatarUrl: avatarUrl.trim() || null,
        persona: persona.trim() || null,
      };
      return isEdit
        ? apiFetch<AdminAuthorDto>(`/api/admin/authors/${author.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(shared),
          })
        : apiFetch<AdminAuthorDto>('/api/admin/authors', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...shared, slug: slugify(name) }),
          });
    },
    onSuccess: (saved) => {
      onSaved(saved);
      setOpen(false);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t.admin.postEditor.editAuthorDialogTitle({ name: author.name }) : t.admin.postEditor.newAuthorDialogTitle}
          </DialogTitle>
          <DialogDescription>{t.admin.postEditor.authorDialogDescription}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="author-name">{t.admin.postEditor.authorNameLabel}</Label>
            <Input
              id="author-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.admin.postEditor.authorNamePlaceholder}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="author-role">{t.admin.postEditor.authorRoleLabel}</Label>
            <Input
              id="author-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder={t.admin.postEditor.authorRolePlaceholder}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="author-avatar">{t.admin.postEditor.authorAvatarLabel}</Label>
            <Input
              id="author-avatar"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder={t.admin.postEditor.authorAvatarPlaceholder}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="author-bio">{t.admin.postEditor.authorBioLabel}</Label>
            <textarea
              id="author-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={2}
              maxLength={2000}
              className={TEXTAREA_CLASSES}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="author-persona">{t.admin.postEditor.authorPersonaLabel}</Label>
            <textarea
              id="author-persona"
              value={persona}
              onChange={(e) => setPersona(e.target.value)}
              rows={3}
              maxLength={PERSONA_MAX}
              placeholder={t.admin.postEditor.authorPersonaPlaceholder}
              className={TEXTAREA_CLASSES}
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">{t.admin.postEditor.authorPersonaHint}</p>
              <span className="shrink-0 text-xs text-muted-foreground">
                {persona.length} / {PERSONA_MAX}
              </span>
            </div>
          </div>
        </div>
        {error && <p className="text-[13px] text-destructive">{error}</p>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            {t.admin.postEditor.cancelCta}
          </Button>
          <Button
            type="button"
            disabled={!name.trim() || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending
              ? isEdit
                ? t.admin.postEditor.authorSavingCta
                : t.admin.postEditor.authorAddingCta
              : isEdit
                ? t.admin.postEditor.saveAuthorCta
                : t.admin.postEditor.addAuthorCta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Blog-polish spec §G — assembled fresh on every "Copy AI prompt" click (never cached),
 * always reflecting whichever author is CURRENTLY selected in the form. Kept as a pure
 * function (no component state, `t` passed in explicitly) so its exact text can be asserted
 * in a test without mounting the dialog. */
function buildAiPrompt({
  authorName,
  persona,
  t,
}: {
  authorName: string | null;
  persona: string | null;
  t: Catalog;
}): string {
  const trimmedPersona = persona?.trim() ?? '';
  const personaSection = trimmedPersona
    ? t.admin.postEditor.aiPromptPersonaWithVoice({
        authorName: authorName ?? t.admin.postEditor.aiPromptAuthorFallbackLower,
        persona: trimmedPersona,
      })
    : t.admin.postEditor.aiPromptPersonaNoPersona({
        authorName: authorName ?? t.admin.postEditor.aiPromptAuthorFallbackUpper,
      });

  return t.admin.postEditor.aiPromptTemplate({ personaSection });
}

/** Collapsible "Writing guide" panel (spec §G). `packages/ui` has no disclosure/accordion
 * primitive yet, so this uses a plain `<details>`/`<summary>` styled with the same design
 * tokens (border/muted-foreground/etc.) as the rest of the editor — native, no JS needed to
 * open/close, and cheap to add a real primitive on top of later if one shows up in
 * `@repo/ui`. */
function WritingGuidePanel({
  authorName,
  persona,
  t,
}: {
  authorName: string | null;
  persona: string | null;
  t: Catalog;
}) {
  const [copied, setCopied] = useState(false);
  const [copyUnavailable, setCopyUnavailable] = useState(false);

  async function handleCopyPrompt() {
    const prompt = buildAiPrompt({ authorName, persona, t });
    try {
      if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(prompt);
      setCopyUnavailable(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
      setCopyUnavailable(true);
    }
  }

  return (
    <details className="rounded-md border p-4 text-sm [&_summary]:cursor-pointer [&>summary]:list-none [&>summary::-webkit-details-marker]:hidden">
      <summary className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold">{t.admin.postEditor.writingGuideHeading}</span>
        <span className="text-xs font-normal text-muted-foreground">
          {t.admin.postEditor.writingGuideSubtitle}
        </span>
      </summary>

      <div className="mt-4 flex flex-col gap-4">
        <div>
          <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            {t.admin.postEditor.supportedMarkdownHeading}
          </h3>
          <ul className="mt-1.5 list-disc pl-5 text-muted-foreground">
            <li>{t.admin.postEditor.mdListHeadings}</li>
            <li>{t.admin.postEditor.mdListLists}</li>
            <li>
              {t.admin.postEditor.mdListLinksPrefix}
              <code className="rounded bg-muted px-1">[text](url)</code>
              {t.admin.postEditor.mdListLinksSuffix}
            </li>
            <li>{t.admin.postEditor.mdListImages}</li>
            <li>{t.admin.postEditor.mdListCode}</li>
            <li>{t.admin.postEditor.mdListTables}</li>
            <li>{t.admin.postEditor.mdListBlockquotes}</li>
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            {t.admin.postEditor.strippedHeading}
          </h3>
          <p className="mt-1.5 text-muted-foreground">
            {t.admin.postEditor.strippedPrefix}
            <code className="rounded bg-muted px-1">&lt;script&gt;</code>/
            <code className="rounded bg-muted px-1">&lt;style&gt;</code>
            {t.admin.postEditor.strippedSuffix}
          </p>
        </div>

        <div>
          <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            {t.admin.postEditor.imagesHeading}
          </h3>
          <p className="mt-1.5 text-muted-foreground">
            {t.admin.postEditor.imagesPrefix}{' '}
            <code className="rounded bg-muted px-1">![](url)</code>
            {t.admin.postEditor.imagesSuffix}
          </p>
        </div>

        <div className="flex flex-col gap-2 border-t pt-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" size="sm" onClick={handleCopyPrompt}>
              {copied ? t.admin.postEditor.copiedCta : t.admin.postEditor.copyAiPromptCta}
            </Button>
            {copied && <span className="text-xs text-muted-foreground">{t.admin.postEditor.copiedToClipboard}</span>}
            {copyUnavailable && (
              <span className="text-xs text-destructive">
                {t.admin.postEditor.clipboardUnavailable}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t.admin.postEditor.assemblesPrefix}
            {authorName
              ? t.admin.postEditor.assemblesAuthorPersonaClause({ authorName })
              : t.admin.postEditor.assemblesNoAuthorClause}
            {t.admin.postEditor.assemblesSuffix}
          </p>
          {copyUnavailable && (
            <textarea
              readOnly
              rows={10}
              value={buildAiPrompt({ authorName, persona, t })}
              onFocus={(e) => e.currentTarget.select()}
              className={`${TEXTAREA_CLASSES} font-mono text-xs`}
            />
          )}
        </div>
      </div>
    </details>
  );
}
